import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { EffortImpactScale } from './product-roadmap-constants';

interface EffortImpactScalesProps {
  scales: { impact: EffortImpactScale; effort: EffortImpactScale };
}

// Each list runs the way its chart axis does: Impact high-to-low (the vertical axis, top down),
// Effort low-to-high (the horizontal axis, left to right). The data lists both low band first.
const ScaleList = ({
  title,
  scale,
  highFirst
}: {
  title: string;
  scale: EffortImpactScale;
  highFirst: boolean;
}) => {
  const bands = highFirst ? [...scale.bands].reverse() : scale.bands;
  return (
    <div className='effort-impact-scale'>
      <h3>{title}</h3>
      <p className='effort-impact-scale-def'>{scale.definition}</p>
      <ul className='effort-impact-bands'>
        {bands.map(({ band, lead, text }) => (
          <li key={band}>
            <span className='effort-impact-band'>{band}</span>
            <span>
              {lead && text.startsWith(lead) ? (
                <>
                  <strong>{lead}</strong>
                  {text.slice(lead.length)}
                </>
              ) : (
                text
              )}
            </span>
          </li>
        ))}
      </ul>
      {scale.note && <p className='effort-impact-scale-note'>{scale.note}</p>}
    </div>
  );
};

// "How scores work", under the function legend. Closed, the card takes only the height the chart
// leaves beside the legend, so the right column ends flush with the chart, and fades out with a
// "Show full definitions" control (Sean, 2026-10-19: at full height it pushed "By quadrant" far
// down and threw the page out of alignment). Open, it takes its natural height. If everything
// already fits, the fade and the control are dropped. On narrow screens, where the columns stack,
// it always shows in full (see the SCSS).
export const EffortImpactScales = ({ scales }: EffortImpactScalesProps) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [fits, setFits] = useState(false);

  const measure = useCallback(() => {
    const body = bodyRef.current;
    if (!body || open) return;
    setFits(body.scrollHeight <= body.clientHeight + 1);
  }, [open]);

  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return undefined;
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    return () => observer.disconnect();
  }, [measure]);

  return (
    <div
      className={cn('effort-impact-panel', 'effort-impact-scales', {
        'is-open': open,
        'is-fit': fits && !open
      })}
    >
      <h2 className='effort-impact-heading'>How scores work</h2>
      <div className='effort-impact-scales-body' id='effort-impact-scales-body' ref={bodyRef}>
        <ScaleList title='Impact' scale={scales.impact} highFirst />
        <ScaleList title='Effort remaining' scale={scales.effort} highFirst={false} />
      </div>
      {!(fits && !open) && (
        <button
          type='button'
          className='effort-impact-link effort-impact-more'
          aria-expanded={open}
          aria-controls='effort-impact-scales-body'
          onClick={() => setOpen(o => !o)}
        >
          {open ? 'Show less' : 'Show full definitions'}
          <svg className='effort-impact-chevron' viewBox='0 0 16 16' aria-hidden='true'>
            <path
              d='M4 6l4 4 4-4'
              fill='none'
              stroke='currentColor'
              strokeWidth='1.8'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </button>
      )}
    </div>
  );
};
